<script>
import { format } from 'date-fns'
import arLocale from 'date-fns/locale/ar-SA'
import enLocale from 'date-fns/locale/en-US'
import { mapGetters } from 'vuex'
import {
  checkPhoneNumber,
  getCountryCallingCodeByCountryCode,
  findCountryDataByCountryCallingCode,
} from '@/utils/country-data'
import BaseButtonV2 from '@/components/lavender/v2/BaseButton.vue'
import BaseInputV2 from '@/components/lavender/v2/BaseInput.vue'

const INQUIRY_ABOUT_OPTIONS = {
  in_store: '2',
  online: '1',
}

const PURPOSE_OF_CALL_OPTIONS = [
  { label: 'Registration', value: 'registration_and_login' },
  { label: 'Account Management', value: 'account_management' },
  { label: 'Order Management', value: 'order_management' },
  { label: 'Payment and Billings', value: 'payment_and_billings' },
  { label: 'Dispute Management', value: 'dispute_management' },
  { label: 'Other', value: 'other' },
]

const CALLBACK_LANGUAGE_OPTIONS = [
  { value: 'ar', labelKey: 'ads_arabic' },
  { value: 'en', labelKey: 'ads_english' },
]

const SERVICE_TIMEZONE_OFFSET_MINUTES = {
  SA: 3 * 60,
  AE: 4 * 60,
  KW: 3 * 60,
  BH: 3 * 60,
  QA: 3 * 60,
  OM: 4 * 60,
}
const SLOT_STEP_MINUTES = 30
const SERVICE_DAY_START_MINUTES = 9 * 60
const SERVICE_BUSINESS_END_MINUTES = 17 * 60 + 30
const DAY_IN_MS = 24 * 60 * 60 * 1000

function getServiceOffsetMinutes(countryCode) {
  return SERVICE_TIMEZONE_OFFSET_MINUTES[countryCode] || 3 * 60
}

function toOffsetDate(date, offsetMinutes) {
  return new Date(date.getTime() + offsetMinutes * 60 * 1000)
}

function toDayKeyFromOffsetDate(date) {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseDayKey(dayKey) {
  const [year, month, day] = dayKey.split('-').map(Number)
  return { year, month, day }
}

function toServiceUtcDate(dayKey, minuteOfDay, serviceOffsetMinutes) {
  const { year, month, day } = parseDayKey(dayKey)
  const hour = Math.floor(minuteOfDay / 60)
  const minute = minuteOfDay % 60
  const utcMs =
    Date.UTC(year, month - 1, day, hour, minute, 0, 0) -
    serviceOffsetMinutes * 60 * 1000
  return new Date(utcMs)
}

function toLocalDayKey(date) {
  return format(date, 'yyyy-MM-dd')
}

function toTimeRangeValue(startDate, endDate) {
  return `${format(startDate, 'H:mm')}-${format(endDate, 'H:mm')}`
}

export default {
  name: 'SupportCallbackDialog',
  components: {
    BaseInputV2,
    BaseButtonV2,
  },
  props: {
    submitting: {
      type: Boolean,
      default: false,
    },
    merchantProfile: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      phoneNumber: '',
      inquiryAbout: '',
      purposeOfCall: '',
      purposeOfCallOther: '',
      context: '',
      callbackTime: '',
      callbackDay: '',
      callbackLanguage: '',
      errorMessages: {
        phoneNumber: '',
        inquiryAbout: '',
        purposeOfCall: '',
        callbackDay: '',
        callbackTime: '',
        context: '',
      },
      showPurposeBottomSheet: false,
      showLanguageBottomSheet: false,
      showDayBottomSheet: false,
      showTimeBottomSheet: false,
      isMobile: false,
      dropdownObserver: null,
    }
  },
  computed: {
    ...mapGetters('translations', ['currentLocale', 'currentLang']),
    merchantCountryCode() {
      return this.merchantProfile?.country || 'SA'
    },
    countryCode() {
      // Go back to original - just use merchant country
      return getCountryCallingCodeByCountryCode(this.merchantCountryCode)
    },
    inquiryAboutOptions() {
      return [
        {
          label: this.$t(
            'support_widget_request_callback_inquiry_about_in_store'
          ),
          value: INQUIRY_ABOUT_OPTIONS.in_store,
        },
        {
          label: this.$t(
            'support_widget_request_callback_inquiry_about_online'
          ),
          value: INQUIRY_ABOUT_OPTIONS.online,
        },
      ]
    },
    purposeOfCallOptions() {
      return PURPOSE_OF_CALL_OPTIONS.map((option) => ({
        ...option,
        label:
          this.$t(`support_widget_request_callback_purpose_${option.value}`) ||
          option.label,
      }))
    },
    callbackLanguageOptions() {
      return CALLBACK_LANGUAGE_OPTIONS.map((option) => ({
        value: option.value,
        label: this.$t(option.labelKey),
      }))
    },
    locale() {
      return this.currentLocale === 'ar_SA' ? arLocale : enLocale
    },
    serviceOffsetMinutes() {
      return getServiceOffsetMinutes(this.merchantCountryCode)
    },
    serviceNow() {
      return toOffsetDate(new Date(), this.serviceOffsetMinutes)
    },
    serviceCurrentMinutes() {
      return (
        this.serviceNow.getUTCHours() * 60 + this.serviceNow.getUTCMinutes()
      )
    },
    serviceTodayDayKey() {
      return toDayKeyFromOffsetDate(this.serviceNow)
    },
    isAfterWorkingHours() {
      return (
        this.serviceCurrentMinutes >= SERVICE_BUSINESS_END_MINUTES ||
        this.serviceCurrentMinutes < SERVICE_DAY_START_MINUTES
      )
    },
    /**
     * True when at least one 30m callback window in the service (merchant) time
     * zone still has a start in the future for the current service "today".
     * Used to decide whether the day list includes "Today" (not tied to 9:00–17:30 wall clock).
     */
    hasServiceTodaySlotsRemaining() {
      const now = Date.now()
      const dayKey = this.serviceTodayDayKey
      for (
        let minuteOfDay = SERVICE_DAY_START_MINUTES;
        minuteOfDay + SLOT_STEP_MINUTES <= SERVICE_BUSINESS_END_MINUTES;
        minuteOfDay += SLOT_STEP_MINUTES
      ) {
        const slotStartUtc = toServiceUtcDate(
          dayKey,
          minuteOfDay,
          this.serviceOffsetMinutes
        )
        if (slotStartUtc.getTime() > now) {
          return true
        }
      }
      return false
    },
    callbackDayOptions() {
      const relativeByOffset = {
        0: 'today',
        1: 'tomorrow',
        2: 'after_tomorrow',
      }

      const offsets = this.hasServiceTodaySlotsRemaining ? [0, 1, 2] : [1, 2]

      return offsets.map((offset) => {
        const todayMidnightUtc = Date.UTC(
          this.serviceNow.getUTCFullYear(),
          this.serviceNow.getUTCMonth(),
          this.serviceNow.getUTCDate()
        )
        const optionDate = new Date(todayMidnightUtc + offset * DAY_IN_MS)
        const dayKey = toDayKeyFromOffsetDate(optionDate)
        const relativeKey = relativeByOffset[offset]
        return {
          value: dayKey,
          relativeKey,
          serviceDate: optionDate,
          displayDate: format(
            toServiceUtcDate(
              dayKey,
              SERVICE_DAY_START_MINUTES,
              this.serviceOffsetMinutes
            ),
            'EEEE MM/dd',
            { locale: this.locale }
          ),
          label: `${this.$t(
            `support_widget_request_callback_day_${relativeKey}`
          )}`,
        }
      })
    },
    selectedDayOption() {
      return this.callbackDayOptions.find(
        (opt) => opt.value === this.callbackDay
      )
    },
    timeSlotOptions() {
      if (!this.selectedDayOption) {
        return []
      }

      const isTodayInServiceTz = this.callbackDay === this.serviceTodayDayKey
      const slots = []
      const now = Date.now()

      for (
        let minuteOfDay = SERVICE_DAY_START_MINUTES;
        minuteOfDay + SLOT_STEP_MINUTES <= SERVICE_BUSINESS_END_MINUTES;
        minuteOfDay += SLOT_STEP_MINUTES
      ) {
        const slotStartUtc = toServiceUtcDate(
          this.callbackDay,
          minuteOfDay,
          this.serviceOffsetMinutes
        )
        const slotEndUtc = toServiceUtcDate(
          this.callbackDay,
          minuteOfDay + SLOT_STEP_MINUTES,
          this.serviceOffsetMinutes
        )

        if (isTodayInServiceTz && slotStartUtc.getTime() <= now) {
          continue
        }

        const localStart = new Date(slotStartUtc)
        const localEnd = new Date(slotEndUtc)
        const startFormatted = format(localStart, 'h:mm a', {
          locale: this.locale,
        })
        const endFormatted = format(localEnd, 'h:mm a', { locale: this.locale })
        slots.push({
          value: toTimeRangeValue(localStart, localEnd),
          label: `${startFormatted} - ${endFormatted}`,
          payloadDayKey: toLocalDayKey(localStart),
        })
      }

      if (!slots.length) {
        return []
      }

      const asapOption = {
        value: 'asap',
        label: this.$t('support_widget_request_callback_time_asap') || 'ASAP',
        payloadDayKey: this.callbackDay,
      }

      return isTodayInServiceTz ? [asapOption, ...slots] : [...slots]
    },
    selectedTimeOption() {
      return this.timeSlotOptions.find((opt) => opt.value === this.callbackTime)
    },
    selectedPurposeLabel() {
      const option = this.purposeOfCallOptions.find(
        (opt) => opt.value === this.purposeOfCall
      )
      return option ? option.label : ''
    },
    selectedLanguageLabel() {
      const option = this.callbackLanguageOptions.find(
        (opt) => opt.value === this.callbackLanguage
      )
      return option ? option.label : ''
    },
    selectedDayLabel() {
      if (!this.selectedDayOption) {
        return ''
      }
      return `${this.selectedDayOption.label}, ${this.selectedDayOption.displayDate}`
    },
    selectedTimeLabel() {
      return this.selectedTimeOption ? this.selectedTimeOption.label : ''
    },
    showPurposeOtherInput() {
      return this.purposeOfCall === 'other'
    },
    maxLength() {
      return findCountryDataByCountryCallingCode(this.countryCode)
        .numberMaxLength
    },
    supportCallbackFlagIcon() {
      return `flag-${this.merchantCountryCode?.toLowerCase()}`
    },
  },
  watch: {
    inquiryAbout: function(val) {
      if (val) {
        this.clearInquiryAboutError()
      }
    },
    phoneNumber: function(val) {
      if (val) {
        this.clearPhoneNumberError()
      }
    },
    purposeOfCall: function(val) {
      if (val) {
        this.clearPurposeOfCallError()
        if (val !== 'other') {
          this.purposeOfCallOther = ''
          this.context = ''
        }
      }
    },
    purposeOfCallOther: function(val) {
      if (this.purposeOfCall === 'other' && val?.trim()) {
        this.clearPurposeOfCallError()
      }
    },
    context: function(val) {
      if (this.purposeOfCall === 'other') {
        if (val?.trim()) {
          this.clearPurposeOfCallError()
          this.validateContext()
        } else {
          this.clearContextError()
        }
      }
    },
    callbackDay: function(val) {
      if (val) {
        this.clearCallbackDayError()
      }
      this.syncCallbackTimeToDay()
    },
    callbackTime: function(val) {
      if (val) {
        this.clearCallbackTimeError()
      }
    },
    isAfterWorkingHours: function() {
      this.$nextTick(() => {
        const opts = this.callbackDayOptions
        if (!opts.length) {
          this.callbackDay = ''
          this.callbackTime = ''
          return
        }
        if (!opts.some((o) => o.value === this.callbackDay)) {
          this.callbackDay = opts[0].value
        }
        this.syncCallbackTimeToDay()
      })
    },
    currentLang: {
      handler() {
        this.callbackLanguage = this.getDefaultCallbackLanguage()
      },
      immediate: true,
    },
  },
  created() {
    if (this.callbackDayOptions.length) {
      this.callbackDay = this.callbackDayOptions[0].value
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    // Set up observer to remove inline margins from dropdown wraps
    this.setupDropdownMarginObserver()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    if (this.dropdownObserver) {
      this.dropdownObserver.disconnect()
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    validateLength(value, maxLength) {
      if (value.length >= maxLength) {
        return false
      }
      return true
    },
    isNumber(evt) {
      evt = evt || window.event
      const value = evt.target.value
      if (!this.validateLength(value, this.maxLength)) {
        evt.stopPropagation()
        evt.preventDefault()
        return false
      }
      const charCode = evt.which ? evt.which : evt.keyCode
      if (
        (charCode < 1632 || charCode > 1641) && // To allow arabic numbers
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.stopPropagation()
        evt.preventDefault()
        return false
      }
      return true
    },
    validatePhoneNumber() {
      const errorMessage = checkPhoneNumber(this.countryCode, this.phoneNumber)
      if (errorMessage) {
        this.errorMessages.phoneNumber = this.$t(errorMessage)
        return false
      }
      this.errorMessages.phoneNumber = ''
      return true
    },
    validateInquiryAbout() {
      if (!this.inquiryAbout) {
        this.errorMessages.inquiryAbout = this.$t('general_empty_field_message')
        return false
      }
      return true
    },
    validatePurposeOfCall() {
      if (!this.purposeOfCall) {
        this.errorMessages.purposeOfCall = this.$t(
          'general_empty_field_message'
        )
        return false
      }
      if (this.purposeOfCall === 'other') {
        // Clear purpose error since we'll show error on context field
        this.errorMessages.purposeOfCall = ''

        // Check if context is provided
        if (!this.context || !this.context.trim()) {
          this.errorMessages.context = this.$t('general_empty_field_message')
          return false
        }
        // Validate context if provided
        return this.validateContext()
      }
      // Clear context error if not "other"
      this.errorMessages.context = ''
      return true
    },
    validateContext() {
      const contextText = this.context.trim()

      // Check length > 100 characters
      if (contextText.length > 100) {
        this.errorMessages.context =
          this.$t('support_widget_request_callback_context_length_error') ||
          'Context must be 100 characters or less'
        return false
      }

      // Check for special characters (allow letters, numbers, spaces, and basic punctuation)
      // Allowed: letters (including Arabic), numbers, spaces, and: . , ? ! : ; - ( ) ' " / \
      const allowedPattern = /^[\p{L}\p{N}\s.,?!:;\-()'"/\\]+$/u
      if (!allowedPattern.test(contextText)) {
        this.errorMessages.context =
          this.$t(
            'support_widget_request_callback_context_special_chars_error'
          ) || 'Context contains invalid special characters'
        return false
      }

      this.errorMessages.context = ''
      return true
    },
    validateCallbackDay() {
      if (
        !this.callbackDay ||
        !this.callbackDayOptions.some((o) => o.value === this.callbackDay)
      ) {
        this.errorMessages.callbackDay = this.$t('general_empty_field_message')
        return false
      }
      this.errorMessages.callbackDay = ''
      return true
    },
    validateCallbackTime() {
      if (!this.callbackTime) {
        this.errorMessages.callbackTime = this.$t('general_empty_field_message')
        return false
      }
      return true
    },
    syncCallbackTimeToDay() {
      const opts = this.timeSlotOptions
      const stillValid = opts.some(
        (o) => o.value === this.callbackTime && !o.isLabel
      )
      if (!stillValid) {
        this.callbackTime = ''
      }
    },
    clearInquiryAboutError() {
      this.errorMessages.inquiryAbout = ''
    },
    clearPhoneNumberError() {
      this.errorMessages.phoneNumber = ''
    },
    clearPurposeOfCallError() {
      this.errorMessages.purposeOfCall = ''
    },
    clearCallbackDayError() {
      this.errorMessages.callbackDay = ''
    },
    clearCallbackTimeError() {
      this.errorMessages.callbackTime = ''
    },
    clearContextError() {
      this.errorMessages.context = ''
    },
    blurPhoneNumberInput() {
      this.validatePhoneNumber()
    },
    openPurposeBottomSheet() {
      if (this.isMobile) {
        this.showPurposeBottomSheet = true
      }
    },
    closePurposeBottomSheet() {
      this.showPurposeBottomSheet = false
    },
    openLanguageBottomSheet() {
      if (this.isMobile) {
        this.showLanguageBottomSheet = true
      }
    },
    closeLanguageBottomSheet() {
      this.showLanguageBottomSheet = false
    },
    openDayBottomSheet() {
      if (this.isMobile) {
        this.showDayBottomSheet = true
      }
    },
    closeDayBottomSheet() {
      this.showDayBottomSheet = false
    },
    openTimeBottomSheet() {
      if (this.isMobile) {
        this.showTimeBottomSheet = true
      }
    },
    closeTimeBottomSheet() {
      this.showTimeBottomSheet = false
    },
    selectPurpose(value) {
      this.purposeOfCall = value
      if (this.isMobile) {
        this.closePurposeBottomSheet()
      }
    },
    selectLanguage(value) {
      this.callbackLanguage = value
      if (this.isMobile) {
        this.closeLanguageBottomSheet()
      }
    },
    onTimeOptionClick(option) {
      if (option.isLabel || option.disabled) {
        return
      }
      this.selectTime(option.value)
    },
    selectDay(value) {
      this.callbackDay = value
      if (this.isMobile) {
        this.closeDayBottomSheet()
      }
    },
    selectTime(value) {
      this.callbackTime = value
      if (this.isMobile) {
        this.closeTimeBottomSheet()
      }
    },
    handleSubmit() {
      if (
        !this.validatePhoneNumber() ||
        !this.validateInquiryAbout() ||
        !this.validatePurposeOfCall() ||
        !this.validateCallbackDay() ||
        !this.validateCallbackTime()
      ) {
        return
      }
      this.$emit('submit', {
        countryCode: this.countryCode,
        phoneNumber: this.phoneNumber,
        inquiryAbout: this.inquiryAbout,
        purposeOfCall: this.purposeOfCall,
        purposeOfCallOther: this.purposeOfCallOther,
        context: this.context,
        callbackLanguage: this.callbackLanguage,
        callbackTime: this.callbackTime,
        callbackDay: this.selectedTimeOption?.payloadDayKey || this.callbackDay,
      })
    },
    handleCancel() {
      this.$emit('cancel')
      this.clearData()
    },
    clearData() {
      this.phoneNumber = ''
      this.inquiryAbout = ''
      this.purposeOfCall = ''
      this.purposeOfCallOther = ''
      this.context = ''
      this.callbackTime = ''
      this.callbackDay = ''
      this.callbackLanguage = this.getDefaultCallbackLanguage()
      this.errorMessages = {
        phoneNumber: '',
        inquiryAbout: '',
        purposeOfCall: '',
        callbackDay: '',
        callbackTime: '',
        context: '',
      }
      if (this.callbackDayOptions.length) {
        this.callbackDay = this.callbackDayOptions[0].value
      }
    },
    setupDropdownMarginObserver() {
      // Use MutationObserver to watch for dropdown elements and remove inline margins
      this.dropdownObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Check if this is the popper element
              if (
                node.classList &&
                node.classList.contains(
                  'support-callback-dialog__select-popper'
                )
              ) {
                this.$nextTick(() => {
                  this.removeDropdownMargins(node)
                })
              }
              // Also check for nested elements
              const wraps = node.querySelectorAll
                ? node.querySelectorAll(
                    '.el-select-dropdown__wrap, .el-scrollbar__wrap'
                  )
                : []
              wraps.forEach((wrap) => {
                this.removeDropdownMargins(wrap)
              })
            }
          })
        })
      })

      // Start observing the document body for new elements
      this.dropdownObserver.observe(document.body, {
        childList: true,
        subtree: true,
      })
    },
    removeDropdownMargins(element) {
      if (element && element.style) {
        element.style.margin = '0'
        element.style.marginTop = '0'
        element.style.marginRight = '0'
        element.style.marginBottom = '0'
        element.style.marginLeft = '0'
      }
    },
    getDefaultCallbackLanguage() {
      return this.currentLang === 'ar' ? 'ar' : 'en'
    },
  },
}
</script>

<template>
  <div class="support-callback-dialog is-lavender-design">
    <div class="support-callback-dialog__head">
      <div class="support-callback-dialog__head-title">
        {{ $t('sidebar_support_widget_request_callback_title') }}
      </div>
      <div class="support-callback-dialog__head-description">
        {{ $t('sidebar_support_widget_request_callback_description') }}
      </div>
    </div>
    <div class="support-callback-dialog__body">
      <!-- Phone Number -->
      <div class="support-callback-dialog__body-item__contact">
        <label class="support-callback-dialog__body-item__contact-label">
          {{ $t('sidebar_support_widget_request_callback_phone_number_label') }}
        </label>
        <BaseInputV2
          v-model="phoneNumber"
          type="tel"
          pattern="[0-9]*"
          inputmode="numeric"
          :maxlength="maxLength"
          :placeholder="
            $t(
              'sidebar_support_widget_request_callback_phone_number_placeholder'
            )
          "
          :disabled="submitting"
          class="support-callback-dialog__body-item__contact-input"
          @keypress.native.stop="isNumber"
          @blur="blurPhoneNumberInput"
        >
          <template v-slot:prepend>
            <svg-icon
              :name="supportCallbackFlagIcon"
              width="24px"
              height="24px"
            />
            <span class="support-callback-dialog__phone-code">
              +{{ countryCode }}
            </span>
          </template>
        </BaseInputV2>
        <Transition mode="out-in" name="fade">
          <div
            v-show="errorMessages.phoneNumber"
            class="support-callback-dialog__body-item__contact-error"
          >
            {{ errorMessages.phoneNumber }}
          </div>
        </Transition>
      </div>

      <!-- Preferred Language -->
      <div class="support-callback-dialog__body-item__language">
        <label class="support-callback-dialog__body-item__language-label">
          {{ $t('sidebar_support_widget_request_callback_language_label') }}
        </label>
        <div v-if="!isMobile" class="support-callback-dialog__select-wrapper">
          <el-select
            v-model="callbackLanguage"
            :disabled="submitting"
            :placeholder="$t('general_select_placeholder')"
            popper-class="support-callback-dialog__select-popper"
            class="support-callback-dialog__select"
          >
            <el-option
              v-for="option in callbackLanguageOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </div>
        <div
          v-else
          class="support-callback-dialog__select-trigger"
          @click="openLanguageBottomSheet"
        >
          <span :class="{ 'is-placeholder': !callbackLanguage }">
            {{ callbackLanguage ? selectedLanguageLabel : 'Select' }}
          </span>
          <svg-icon name="chevron-down" width="16px" height="16px" />
        </div>
      </div>

      <!-- Inquiry About -->
      <div
        class="support-callback-dialog__body-item__inquiry-about"
        :class="{
          'support-callback-dialog__body-item__inquiry-about--error':
            errorMessages.inquiryAbout,
        }"
      >
        <label class="support-callback-dialog__body-item__inquiry-about-label">
          {{
            $t('sidebar_support_widget_request_callback_inquiry_about_label')
          }}
        </label>
        <div class="support-callback-dialog__radio-group">
          <div
            v-for="option in inquiryAboutOptions"
            :key="option.value"
            class="support-callback-dialog__radio-option"
            :class="{
              'is-selected': inquiryAbout === option.value,
            }"
            @click="!submitting && (inquiryAbout = option.value)"
          >
            <div class="support-callback-dialog__radio-button">
              <div
                class="support-callback-dialog__radio-dot"
                :class="{ 'is-selected': inquiryAbout === option.value }"
              />
            </div>
            <span class="support-callback-dialog__radio-label">
              {{ option.label }}
            </span>
          </div>
        </div>
        <Transition mode="out-in" name="fade">
          <div
            v-show="errorMessages.inquiryAbout"
            class="support-callback-dialog__body-item__contact-error"
          >
            {{ errorMessages.inquiryAbout }}
          </div>
        </Transition>
      </div>

      <!-- Purpose of Call -->
      <div
        class="support-callback-dialog__body-item__purpose"
        :class="{
          'support-callback-dialog__body-item__purpose--error':
            errorMessages.purposeOfCall,
        }"
      >
        <label class="support-callback-dialog__body-item__purpose-label">
          {{ $t('sidebar_support_widget_request_callback_purpose_label') }}
        </label>
        <div v-if="!isMobile" class="support-callback-dialog__select-wrapper">
          <el-select
            v-model="purposeOfCall"
            :disabled="submitting"
            :placeholder="$t('general_select_placeholder')"
            popper-class="support-callback-dialog__select-popper"
            class="support-callback-dialog__select"
          >
            <el-option
              v-for="option in purposeOfCallOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </div>
        <div
          v-else
          class="support-callback-dialog__select-trigger"
          @click="openPurposeBottomSheet"
        >
          <span :class="{ 'is-placeholder': !purposeOfCall }">
            {{ purposeOfCall ? selectedPurposeLabel : 'Select' }}
          </span>
          <svg-icon name="chevron-down" width="16px" height="16px" />
        </div>
        <Transition mode="out-in" name="fade">
          <div
            v-show="errorMessages.purposeOfCall"
            class="support-callback-dialog__body-item__contact-error"
          >
            {{ errorMessages.purposeOfCall }}
          </div>
        </Transition>
      </div>

      <!-- Context Textarea -->
      <!-- <Transition mode="out-in" name="fade"> -->
      <div
        v-if="showPurposeOtherInput"
        class="support-callback-dialog__body-item__context"
      >
        <label class="support-callback-dialog__body-item__context-label">
          {{ $t('sidebar_support_widget_request_callback_context_label') }}
        </label>
        <el-input
          v-model="context"
          type="textarea"
          :rows="4"
          :disabled="submitting"
          :placeholder="
            $t('sidebar_support_widget_request_callback_context_placeholder')
          "
          class="support-callback-dialog__textarea"
        />
        <Transition mode="out-in" name="fade">
          <div
            v-show="errorMessages.context"
            class="support-callback-dialog__body-item__contact-error"
          >
            {{ errorMessages.context }}
          </div>
        </Transition>
      </div>
      <!-- </Transition> -->

      <!-- Callback day & time -->
      <div
        v-if="callbackDayOptions.length"
        class="support-callback-dialog__day-time-row"
      >
        <div
          class="support-callback-dialog__body-item__callback-day"
          :class="{
            'support-callback-dialog__body-item__callback-day--error':
              errorMessages.callbackDay,
          }"
        >
          <label class="support-callback-dialog__body-item__callback-day-label">
            {{ $t('sidebar_support_widget_request_callback_day_label') }}
          </label>
          <div v-if="!isMobile" class="support-callback-dialog__select-wrapper">
            <el-select
              v-model="callbackDay"
              :disabled="submitting"
              :placeholder="$t('general_select_placeholder')"
              popper-class="support-callback-dialog__select-popper"
              class="support-callback-dialog__select"
            >
              <el-option
                v-for="option in callbackDayOptions"
                :key="option.value"
                :label="`${option.label}, ${option.displayDate}`"
                :value="option.value"
              />
            </el-select>
          </div>
          <div
            v-else
            class="support-callback-dialog__select-trigger"
            @click="openDayBottomSheet"
          >
            <span :class="{ 'is-placeholder': !callbackDay }">
              {{ callbackDay ? selectedDayLabel : 'Select' }}
            </span>
            <svg-icon name="chevron-down" width="16px" height="16px" />
          </div>
          <Transition mode="out-in" name="fade">
            <div
              v-show="errorMessages.callbackDay"
              class="support-callback-dialog__body-item__contact-error"
            >
              {{ errorMessages.callbackDay }}
            </div>
          </Transition>
        </div>
        <div
          class="support-callback-dialog__body-item__time"
          :class="{
            'support-callback-dialog__body-item__time--error':
              errorMessages.callbackTime,
          }"
        >
          <label class="support-callback-dialog__body-item__time-label">
            {{ $t('sidebar_support_widget_request_callback_time_slot_label') }}
          </label>
          <div v-if="!isMobile" class="support-callback-dialog__select-wrapper">
            <el-select
              v-model="callbackTime"
              :disabled="submitting"
              :placeholder="$t('general_select_placeholder')"
              popper-class="support-callback-dialog__select-popper"
              class="support-callback-dialog__select"
            >
              <el-option
                v-for="option in timeSlotOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
                :disabled="option.disabled"
                :class="{
                  'support-callback-dialog__select-option-label':
                    option.isLabel,
                }"
              />
            </el-select>
          </div>
          <div
            v-else
            class="support-callback-dialog__select-trigger"
            @click="openTimeBottomSheet"
          >
            <span :class="{ 'is-placeholder': !callbackTime }">
              {{ callbackTime ? selectedTimeLabel : 'Select' }}
            </span>
            <svg-icon name="chevron-down" width="16px" height="16px" />
          </div>
          <Transition mode="out-in" name="fade">
            <div
              v-show="errorMessages.callbackTime"
              class="support-callback-dialog__body-item__contact-error"
            >
              {{ errorMessages.callbackTime }}
            </div>
          </Transition>
        </div>
      </div>
    </div>
    <div class="support-callback-dialog__footer">
      <BaseButtonV2
        type="primary"
        size="medium"
        :disabled="submitting"
        :loading="submitting"
        class="support-callback-dialog__footer-submit"
        track-id="support_callback_submit"
        @click="handleSubmit"
      >
        {{ $t('sidebar_support_widget_request_callback_submit') }}
      </BaseButtonV2>
    </div>

    <!-- Mobile Bottom Sheets -->
    <el-drawer
      v-if="isMobile"
      :visible.sync="showPurposeBottomSheet"
      :with-header="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      direction="btt"
      size="auto"
      custom-class="support-callback-dialog__bottom-sheet"
      @close="closePurposeBottomSheet"
    >
      <div class="support-callback-dialog__bottom-sheet-content">
        <div class="support-callback-dialog__bottom-sheet-header">
          <div class="support-callback-dialog__bottom-sheet-drag-handle" />
          <div class="support-callback-dialog__bottom-sheet-title">
            {{ $t('sidebar_support_widget_request_callback_purpose_label') }}
          </div>
          <BaseButtonV2
            circle
            type="secondary"
            size="small"
            class="support-callback-dialog__bottom-sheet-close"
            @click="closePurposeBottomSheet"
          >
            <svg-icon name="close" width="16px" height="16px" />
          </BaseButtonV2>
        </div>
        <div class="support-callback-dialog__bottom-sheet-options">
          <div
            v-for="option in purposeOfCallOptions"
            :key="option.value"
            class="support-callback-dialog__bottom-sheet-option"
            :class="{ 'is-selected': purposeOfCall === option.value }"
            @click="selectPurpose(option.value)"
          >
            <div class="support-callback-dialog__radio-button">
              <div
                class="support-callback-dialog__radio-dot"
                :class="{ 'is-selected': purposeOfCall === option.value }"
              />
            </div>
            <span class="support-callback-dialog__radio-label">
              {{ `${option.label}, ${option.displayDate}` }}
            </span>
          </div>
          <Transition mode="out-in" name="fade">
            <div
              v-if="showPurposeOtherInput"
              class="support-callback-dialog__bottom-sheet-other-input"
            >
              <BaseInputV2
                v-model="purposeOfCallOther"
                :disabled="submitting"
                :placeholder="
                  $t(
                    'support_widget_request_callback_purpose_other_placeholder'
                  )
                "
                class="support-callback-dialog__body-item__contact-input"
              />
            </div>
          </Transition>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      v-if="isMobile"
      :visible.sync="showLanguageBottomSheet"
      :with-header="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      direction="btt"
      size="auto"
      custom-class="support-callback-dialog__bottom-sheet"
      @close="closeLanguageBottomSheet"
    >
      <div class="support-callback-dialog__bottom-sheet-content">
        <div class="support-callback-dialog__bottom-sheet-header">
          <div class="support-callback-dialog__bottom-sheet-drag-handle" />
          <div class="support-callback-dialog__bottom-sheet-title">
            {{ $t('sidebar_support_widget_request_callback_language_label') }}
          </div>
          <BaseButtonV2
            circle
            type="secondary"
            size="small"
            class="support-callback-dialog__bottom-sheet-close"
            @click="closeLanguageBottomSheet"
          >
            <svg-icon name="close" width="16px" height="16px" />
          </BaseButtonV2>
        </div>
        <div class="support-callback-dialog__bottom-sheet-options">
          <div
            v-for="option in callbackLanguageOptions"
            :key="option.value"
            class="support-callback-dialog__bottom-sheet-option"
            :class="{ 'is-selected': callbackLanguage === option.value }"
            @click="selectLanguage(option.value)"
          >
            <div class="support-callback-dialog__radio-button">
              <div
                class="support-callback-dialog__radio-dot"
                :class="{ 'is-selected': callbackLanguage === option.value }"
              />
            </div>
            <span class="support-callback-dialog__radio-label">
              {{ option.label }}
            </span>
          </div>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      v-if="isMobile"
      :visible.sync="showDayBottomSheet"
      :with-header="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      direction="btt"
      size="auto"
      custom-class="support-callback-dialog__bottom-sheet"
      @close="closeDayBottomSheet"
    >
      <div class="support-callback-dialog__bottom-sheet-content">
        <div class="support-callback-dialog__bottom-sheet-header">
          <div class="support-callback-dialog__bottom-sheet-drag-handle" />
          <div class="support-callback-dialog__bottom-sheet-title">
            {{ $t('sidebar_support_widget_request_callback_day_label') }}
          </div>
          <BaseButtonV2
            circle
            type="secondary"
            size="small"
            class="support-callback-dialog__bottom-sheet-close"
            @click="closeDayBottomSheet"
          >
            <svg-icon name="close" width="16px" height="16px" />
          </BaseButtonV2>
        </div>
        <div class="support-callback-dialog__bottom-sheet-options">
          <div
            v-for="option in callbackDayOptions"
            :key="option.value"
            class="support-callback-dialog__bottom-sheet-option"
            :class="{ 'is-selected': callbackDay === option.value }"
            @click="selectDay(option.value)"
          >
            <div class="support-callback-dialog__radio-button">
              <div
                class="support-callback-dialog__radio-dot"
                :class="{ 'is-selected': callbackDay === option.value }"
              />
            </div>
            <span class="support-callback-dialog__radio-label">
              {{ option.label }}
            </span>
          </div>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      v-if="isMobile"
      :visible.sync="showTimeBottomSheet"
      :with-header="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      direction="btt"
      size="auto"
      custom-class="support-callback-dialog__bottom-sheet"
      @close="closeTimeBottomSheet"
    >
      <div class="support-callback-dialog__bottom-sheet-content">
        <div class="support-callback-dialog__bottom-sheet-header">
          <div class="support-callback-dialog__bottom-sheet-drag-handle" />
          <div class="support-callback-dialog__bottom-sheet-title">
            {{ $t('sidebar_support_widget_request_callback_time_slot_label') }}
          </div>
          <BaseButtonV2
            circle
            type="secondary"
            size="small"
            class="support-callback-dialog__bottom-sheet-close"
            @click="closeTimeBottomSheet"
          >
            <svg-icon name="close" width="16px" height="16px" />
          </BaseButtonV2>
        </div>
        <div class="support-callback-dialog__bottom-sheet-options">
          <div
            v-for="option in timeSlotOptions"
            :key="option.value"
            class="support-callback-dialog__bottom-sheet-option"
            :class="{
              'is-selected': callbackTime === option.value && !option.isLabel,
              'is-label': option.isLabel,
            }"
            @click="onTimeOptionClick(option)"
          >
            <template v-if="option.isLabel">
              <span class="support-callback-dialog__bottom-sheet-label">
                {{ option.label }}
              </span>
            </template>
            <template v-else>
              <div class="support-callback-dialog__radio-button">
                <div
                  class="support-callback-dialog__radio-dot"
                  :class="{ 'is-selected': callbackTime === option.value }"
                />
              </div>
              <span class="support-callback-dialog__radio-label">
                {{ option.label }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/lavender/_typography.scss';

.support-callback-dialog {
  &__head {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__head-title {
    // Figma: English/Heading/medium - 20px, 700, 28px line-height (140%), -0.3px letter-spacing
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px; // 140% of 20px
    color: var(--tamara-color-content-high);
    letter-spacing: -0.3px;
  }

  &__head-description {
    @extend .tamara-english-regular-2;

    color: var(--tamara-color-content-mid);
    word-break: break-word;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 20px;
  }

  &__day-time-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__body-item {
    &__contact-error {
      @extend .tamara-english-regular-2;

      margin-top: 8px;
      color: var(--tamara-color-error);
    }

    &__contact-label,
    &__inquiry-about-label,
    &__language-label,
    &__purpose-label,
    &__context-label,
    &__callback-day-label,
    &__time-label {
      @extend .tamara-english-regular-2;

      display: block;
      margin-bottom: 0;
      color: var(--tamara-color-content-mid);
    }

    // &__contact-label::after {
    //   color: var(--tamara-color-brand-700);
    //   content: '*';
    //   margin-left: 4px;
    // }

    &__contact {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__phone-input-wrapper {
      display: flex;
      flex-direction: row;
      gap: 8px;
      align-items: center;
      padding: 8px 12px;
      background-color: var(--tamara-color-background-bright);
      border: 1.5px solid var(--tamara-color-border-low);
      border-radius: 12px;

      &:focus-within {
        border: 2px solid var(--tamara-color-brand-700);
      }
    }

    &__phone-prefix {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    &__phone-code {
      @extend .tamara-body-large;

      color: var(--tamara-color-content-high);
    }

    &__phone-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__phone-country-display {
      width: fit-content;
    }

    &__country-selector {
      ::v-deep .el-select {
        .el-input__inner {
          height: auto;
          padding: 0;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
          color: var(--tamara-color-content-mid);
          background: transparent;
          border: none;
        }

        .el-input__prefix {
          position: static;
          margin-right: 8px;
        }

        .el-input__suffix {
          display: none; // Hide dropdown arrow if you want it to be display-only
        }
      }
    }

    &__contact-input {
      max-width: 320px;

      ::v-deep .el-input-group__prepend {
        display: flex;
        gap: 8px;
        align-items: center;
        padding: 0 12px;
        background: transparent;
        border: none;
      }
    }

    &__inquiry-about {
      display: flex;
      flex-direction: column;
      gap: 8px;

      &--error {
        .support-callback-dialog__radio-option {
          border-color: var(--tamara-color-system-content-error);
        }
      }
    }

    &__callback-day,
    &__language,
    &__purpose,
    &__time {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 8px;
      width: 100%;
      min-width: 0;

      &--error {
        .support-callback-dialog__select-wrapper,
        .support-callback-dialog__select-trigger {
          border-color: var(--tamara-color-system-content-error);
        }
      }
    }

    &__purpose-other {
      margin-top: 12px;
    }

    &__context {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }

  &__radio-group {
    display: flex;
    gap: 12px;
  }

  &__radio-option {
    display: flex;
    gap: 8px;
    align-items: center;
    width: 100%;
    padding: 8px 16px;
    cursor: pointer;
    background-color: var(--tamara-color-background-bright);
    border: 1.5px solid var(--tamara-color-border-low);
    border-radius: 12px;
    transition: all 0.2s;

    &:hover {
      border-color: var(--tamara-color-brand-700);
    }

    &.is-selected {
      background-color: var(--tamara-color-system-subtle-brand);
      border-color: var(--tamara-color-brand-700);
    }
  }

  &__radio-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: var(--tamara-color-background-bright);
    border: 2.5px solid var(--tamara-color-background-neutral-high);
    border-radius: 50%;
  }

  &__radio-dot {
    width: 10px;
    height: 10px;
    background-color: transparent;
    border-radius: 50%;
    transition: all 0.2s;

    &.is-selected {
      background-color: var(--tamara-color-brand-700);
    }
  }

  &__radio-label {
    @extend .tamara-english-regular-2;

    color: var(--tamara-color-content-high);
  }

  &__select-wrapper {
    width: 100%;
  }

  &__select {
    width: 100%;

    ::v-deep .el-input__inner {
      height: 46px;
      padding: 8px 12px;
      font-size: 16px;
      color: var(--tamara-color-content-high);
      border: 1.5px solid var(--tamara-color-border-low);
      border-radius: 12px;

      &:focus {
        border: 2px solid var(--tamara-color-brand-700);
      }
    }
  }

  &__select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 46px;
    padding: 8px 12px;
    cursor: pointer;
    background-color: var(--tamara-color-background-bright);
    border: 1.5px solid var(--tamara-color-border-low);
    border-radius: 12px;

    span {
      @extend .tamara-body-large;

      color: var(--tamara-color-content-high);

      &.is-placeholder {
        color: var(--tamara-color-content-low);
      }
    }
  }

  &__textarea {
    ::v-deep .el-textarea__inner {
      padding: 12px;
      font-size: 16px;
      color: var(--tamara-color-content-high);
      resize: vertical;
      border: 1.5px solid var(--tamara-color-border-low);
      border-radius: 12px;

      &:focus {
        border: 2px solid var(--tamara-color-brand-700);
      }
    }
  }

  &__footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-top: 40px;

    &-submit {
      width: 100%;
      padding-inline: 48px;
    }
  }

  // Bottom Sheet Styles
  &__bottom-sheet-content {
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    padding: 16px;
    background-color: var(--tamara-color-background-bright);
    border-radius: 20px 20px 0 0;
  }

  &__bottom-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  &__bottom-sheet-drag-handle {
    position: absolute;
    top: 8px;
    left: 50%;
    width: 40px;
    height: 4px;
    background-color: var(--tamara-color-border-low);
    border-radius: 2px;
    transform: translateX(-50%);
  }

  &__bottom-sheet-title {
    @extend .tamara-english-heading-5;

    flex: 1;
    color: var(--tamara-color-content-high);
    text-align: center;
  }

  &__bottom-sheet-close {
    position: absolute;
    right: 16px;

    [dir='rtl'] & {
      right: auto;
      left: 16px;
    }
  }

  &__bottom-sheet-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 60vh;
    overflow-y: auto;
  }

  &__bottom-sheet-option {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 12px;
    cursor: pointer;
    border-radius: 12px;
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--tamara-color-background-neutral-low);
    }

    &.is-selected {
      background-color: var(--tamara-color-system-subtle-brand);
    }

    &.is-label {
      cursor: default;
      background-color: transparent;
    }
  }

  &__bottom-sheet-label {
    @extend .tamara-english-regular-2;

    font-weight: 600;
    color: var(--tamara-color-content-mid);
  }

  &__bottom-sheet-other-input {
    padding: 0 12px;
    margin-top: 12px;
  }
}

// Global styles for select popper
::v-deep .support-callback-dialog__select-popper {
  border: 1.5px solid var(--tamara-color-border-low);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  .el-select-dropdown__wrap,
  .el-scrollbar__wrap {
    padding: 0 !important;
    margin: 0 !important;
    margin-top: 0 !important;
    margin-right: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
  }

  .el-select-dropdown__item {
    padding: 12px 16px;
    color: var(--tamara-color-content-high);

    &:hover {
      background-color: var(--tamara-color-background-neutral-low);
    }

    &.selected {
      color: var(--tamara-color-brand-700);
      background-color: var(--tamara-color-system-subtle-brand);
    }
  }

  .support-callback-dialog__select-option-label {
    font-weight: 600;
    color: var(--tamara-color-content-mid);
    cursor: default;
  }
}

// Bottom sheet drawer styles
::v-deep .support-callback-dialog__bottom-sheet {
  .el-drawer__body {
    padding: 0;
  }
}
</style>

<style lang="scss">
// Global non-scoped styles to override Element UI inline styles
.support-callback-dialog__select-popper {
  .el-scrollbar__wrap {
    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
    -ms-overflow-style: none;
  }
}
</style>
